import { combatCenterDistanceFt } from '../../../ft3d/engine/ft3d-combat-distance-core.js';
import { encounterCreatureField, inventoryField, layerOf, posCoord, reachOf, sizeIdOf } from './array-types';

type SizeReader = (id: string) => number | null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const record = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;

export function proximityEntityId(row: unknown): string {
    return upper(inventoryField(row, 'inventory_id') ?? encounterCreatureField(record(row)?.encounter_creatures, 'encounter_creature_id'));
}

/** Canonical actor blobs lean-strip zero axes, including an all-zero position bucket. */
export function proximityDistanceFt(source: unknown, target: unknown, size: SizeReader): number | null {
    const position = (row: unknown): { x: number; y: number; z: number } | null => {
        const r = record(row);
        if (!r || (!proximityEntityId(row) && !record(r.position) && !record(r.pos_arr))) return null;
        const coords = record(r.position) ?? record(r.pos_arr);
        for (const axis of ['x_pos', 'y_pos', 'z_pos'] as const) {
            if (coords?.[axis] != null && coords[axis] !== '' && posCoord(row, axis) === undefined) return null;
        }
        const x = Number(posCoord(row, 'x_pos') ?? 0);
        const y = Number(posCoord(row, 'y_pos') ?? 0);
        const z = Number(posCoord(row, 'z_pos') ?? 0);
        return [x, y, z].every(Number.isFinite) ? { x, y, z } : null;
    };
    const from = position(source), to = position(target);
    const sourceSize = size(upper(inventoryField(source, 'inventory_size') || sizeIdOf(source)));
    const targetSize = size(upper(inventoryField(target, 'inventory_size') || sizeIdOf(target) || layerOf(target, 'size')));
    if (!from || !to || sourceSize === null || targetSize === null
        || !Number.isFinite(sourceSize) || !Number.isFinite(targetSize) || sourceSize < 0 || targetSize < 0) return null;
    return combatCenterDistanceFt(from, to, { sourceSizeFeet: sourceSize, targetSizeFeet: targetSize });
}

/** `minReachFt` widens the source's reach for this check only (object_interaction_range_ft toward an unattended object — the
 *  caller decides when it applies); the larger of the two governs. */
export function proximityWithinReach(source: unknown, target: unknown, size: SizeReader, minReachFt = 0): boolean | null {
    const id = proximityEntityId(source);
    if (id && upper(inventoryField(target, 'possess_id')) === id) return true;
    const sourceEncounter = upper(encounterCreatureField(record(source)?.encounter_creatures, 'encounter_id'));
    const targetEncounter = upper(encounterCreatureField(record(target)?.encounter_creatures, 'encounter_id'));
    if (sourceEncounter && targetEncounter && sourceEncounter !== targetEncounter) return false;
    const distance = proximityDistanceFt(source, target, size);
    const reach = Number(reachOf(source) ?? 5);
    const widened = Number.isFinite(minReachFt) ? Math.max(0, minReachFt) : 0;
    return distance === null || !Number.isFinite(reach) ? null : distance <= Math.max(0, reach, widened) + 1e-6;
}
